import { useSearchParams } from "react-router";
import { FolderPlus, Eye, Pencil, Trash2, type EvaluatedItemProps, useDialog } from "@repo/ui";
import { URL_PARAM_VIEW_ALL_BRAND_TASKS } from "~/pages/dashboard/task/tab-windows/utils/window-helpers";
import { useFolderMutations } from "./hooks/use-folder-mutations";
import { useSidebarContext } from "./sidebar-context";

export const useSidebarActions = (
  itemLabel: string,
  entityId?: string,
  entityType?: "brand" | "folder" | "task"
) => {
  const [_, setSearchParams] = useSearchParams();
  const { openFolderDialog } = useSidebarContext();
  const { deleteFolder } = useFolderMutations();
  const { showDialog, closeDialog } = useDialog();
  const actions: EvaluatedItemProps[] = [];

  let brandId: number | undefined;
  let folderId: number | undefined;
  let taskId: number | undefined;

  if (entityId) {
    if (entityType === "brand") {
      brandId = parseInt(entityId.replace("brand-", ""), 10);
    } else if (entityType === "folder") {
      const parts = entityId.replace("folder-", "").split("-");
      if (parts.length >= 2) {
        brandId = parseInt(parts[0]!, 10);
        folderId = parseInt(parts[1]!, 10);
      }
    } else if (entityType === "task") {
      taskId = parseInt(entityId.replace("task-", ""), 10);
    }
  }

  if (entityType === "brand" && brandId) {
    actions.push(
      {
        id: "create-folder",
        label: "Create Task Folder",
        icon: <FolderPlus className="h-4 w-4" />,
        onClick: () => openFolderDialog({ mode: "create", brandId, parentFolderId: null }),
      },
      {
        id: "view-all",
        label: "View All Tasks",
        icon: <Eye className="h-4 w-4" />,
        onClick: () => {
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            const current = newParams.get(URL_PARAM_VIEW_ALL_BRAND_TASKS);
            const brandIds = current ? current.split(",").filter(Boolean) : [];
            if (!brandIds.includes(`${brandId}`)) {
              brandIds.push(`${brandId}`);
            }
            newParams.set(URL_PARAM_VIEW_ALL_BRAND_TASKS, brandIds.join(","));
            return newParams;
          });
        },
      }
    );
  } else if (entityType === "folder" && brandId && folderId) {
    actions.push(
      {
        id: "create-subfolder",
        label: "Create Task Folder",
        icon: <FolderPlus className="h-4 w-4" />,
        onClick: () => openFolderDialog({ mode: "create", brandId, parentFolderId: folderId }),
      },
      {
        id: "rename-folder",
        label: "Rename Folder",
        icon: <Pencil className="h-4 w-4" />,
        onClick: () =>
          openFolderDialog({
            mode: "rename",
            brandId,
            folderId,
            folderName: itemLabel,
          }),
      },
      {
        id: "delete-folder",
        label: "Delete Folder",
        icon: <Trash2 className="h-4 w-4" />,
        destructive: true,
        onClick: () => {
          showDialog({
            title: "Delete Folder",
            description: `Are you sure you want to delete folder "${itemLabel}"?`,
            confirmText: "Delete folder",
            confirmVariant: "destructive",
            cancelText: "Go Back",
            onConfirm: async () => {
              await deleteFolder.mutateAsync({ brandId: brandId!, folderId: folderId! });
              closeDialog();
            },
          });
        },
      }
    );
  } else if (entityType === "task" && taskId) {
    actions.push({
      id: "rename-task",
      label: "Rename Task",
      icon: <Pencil className="h-4 w-4" />,
      onClick: () =>
        openFolderDialog({
          mode: "rename-task",
          taskId,
          taskName: itemLabel,
        }),
    });
  }

  return actions;
};
